<template>
  <div class="musubi-page musubi-page--voice">
    <header class="musubi-page__head">
      <h1>Voice &amp; goals</h1>
      <p class="text-tertiary">
        Tell Yorlin how you want to sound and what you're aiming for. Every AI surface —
        campaign drafts, landing copy and outreach openers — writes in this voice.
      </p>
    </header>

    <div class="vg-card">
      <div v-if="loading" class="text-tertiary small py-3">Loading…</div>
      <template v-else>
        <div v-if="saved" class="vg-banner vg-banner--ok"><CygnusIcon name="check" class="me-1" />Saved. New AI copy will use this voice.</div>
        <div v-if="error" class="vg-banner vg-banner--err"><CygnusIcon name="warning" class="me-1" />{{ error }}</div>

        <label class="vg-field">
          <span class="vg-label">Tone of voice</span>
          <input v-model.trim="form.tone_of_voice" type="text" class="vg-input" placeholder="e.g. friendly, plain-English, no jargon" />
          <span class="vg-hint">A short description of how your emails and pages should read.</span>
        </label>

        <label class="vg-field">
          <span class="vg-label">Business goals</span>
          <textarea v-model.trim="form.business_goals" rows="3" class="vg-input" placeholder="e.g. book more discovery calls with local trades; win repeat work"></textarea>
          <span class="vg-hint">What the AI should steer your outreach toward.</span>
        </label>

        <label class="vg-field">
          <span class="vg-label">What you sell</span>
          <input v-model.trim="form.sells" type="text" class="vg-input" placeholder="e.g. bespoke joinery for homeowners" />
          <span class="vg-hint">Your core offer, so drafts pitch the right thing.</span>
        </label>

        <div class="vg-actions">
          <button class="btn btn-commit" :disabled="saving" @click="save">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="saving" />
            <CygnusIcon name="download" class="me-1" v-else />Save voice &amp; goals
          </button>
          <p class="vg-fineprint">
            <CygnusIcon name="bolt" class="me-1" />Leaving these blank is fine — Yorlin just writes in a neutral, professional voice.
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * AdminVoiceGoals — the "Voice & goals" settings surface (Content & brand group,
 * Ops::SETTINGS). Edits the tenant's tone_of_voice / business_goals / sells,
 * which VoiceProfileService folds into {voice_profile} for every AI copy prompt.
 * Reads/writes GET/POST /api/v1/me/company (JWT-only, tenant-scoped).
 */
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: "AdminVoiceGoals",
  props: { apiBase: { type: String, default: "/api/v1" }, componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loading: true,
      saving: false,
      saved: false,
      error: "",
      form: { tone_of_voice: "", business_goals: "", sells: "" },
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    unwrap(res) { return res?.data?.data ?? res?.data ?? res; },
    async load() {
      this.loading = true;
      try {
        const d = this.unwrap(await this.api.get(this.apiBase + "/me/company"));
        const c = (d && d.company) || {};
        this.form.tone_of_voice = c.tone_of_voice || "";
        this.form.business_goals = c.business_goals || "";
        this.form.sells = c.sells || "";
      } catch (e) { this.error = "Could not load your voice profile."; }
      finally { this.loading = false; }
    },
    async save() {
      this.saving = true; this.saved = false; this.error = "";
      try {
        await this.api.post(this.apiBase + "/me/company", {
          tone_of_voice: this.form.tone_of_voice,
          business_goals: this.form.business_goals,
          sells: this.form.sells,
        });
        this.saved = true;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Could not save just now.";
      } finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.musubi-page--voice { padding: var(--m-space-6, 32px); max-width: 680px; }
.musubi-page__head h1 { font-weight: 750; }
.text-tertiary { color: var(--m-text-tertiary); }
.vg-card {
  margin-top: 18px; background: var(--m-bg-card); border: 1px solid var(--m-border);
  border-radius: var(--m-radius-lg); box-shadow: var(--m-shadow-card); padding: 22px;
}
.vg-banner { border-radius: var(--m-radius); padding: 9px 12px; font-size: 0.82rem; margin-bottom: 14px; }
.vg-banner--ok { background: color-mix(in srgb, var(--m-status-success) 14%, var(--m-bg-card)); color: var(--m-status-success); border: 1px solid color-mix(in srgb, var(--m-status-success) 34%, transparent); }
.vg-banner--err { background: color-mix(in srgb, var(--m-status-danger) 12%, var(--m-bg-card)); color: var(--m-status-danger); border: 1px solid color-mix(in srgb, var(--m-status-danger) 32%, transparent); }
.vg-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 18px; }
.vg-label { font-size: 0.78rem; font-weight: 700; color: var(--m-text-secondary); }
.vg-input {
  border: 1px solid var(--m-border-strong); border-radius: var(--m-radius); padding: 9px 11px;
  font: inherit; font-size: 0.9rem; color: var(--m-text-primary); background: var(--m-bg-content); resize: vertical;
}
.vg-input:focus { outline: none; border-color: var(--m-accent); }
.vg-hint { font-size: 0.72rem; color: var(--m-text-tertiary); }
.vg-actions { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-top: 4px; }
.btn { display: inline-flex; align-items: center; font: inherit; font-weight: 650; border-radius: 8px; padding: 9px 16px; cursor: pointer; border: 1px solid transparent; }
.btn:disabled { opacity: .6; cursor: not-allowed; }
.btn-commit { background: var(--m-accent); color: var(--m-accent-text); border-color: var(--m-accent); font-weight: 700; }
.btn-commit:hover:not(:disabled) { background: var(--m-accent-hover); }
.vg-fineprint { font-size: 0.74rem; color: var(--m-text-tertiary); margin: 0; }
</style>
